import { Link, Chip, Box, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { Link as RouterLink } from "react-router-dom";
import { Icon } from "@iconify/react";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { TeamActionMenu } from "./TeamActionMenu";
import { TeamMembersActionMenu } from "./TeamMembersActionMenu";
import { TeamPayoutActionMenu } from "./TeamPayoutActionMenu";

const dateFormat = "yyyy-MM-dd hh:mm a";

export const teamsColumns = (isReadOnly) => [
  {
    field: "Actions",
    width: 50,
    sortable: false,
    headerName: "",
    renderCell: ({ id, row }) => {
      return <TeamActionMenu id={id} row={row} />;
    },
  },
  {
    field: "name",
    headerName: "Team Name",
    flex: 1.2,
    minWidth: 180,
    renderCell: ({ value, id }) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{
          p: 0.5,
          borderRadius: 1,
          background: (theme) => theme.palette.primary.lighter,
          mr: 1,
          display: 'flex',
          alignItems: 'center'
        }}>
          <Icon icon="eva:people-outline" width={16} height={16} style={{ 
            color: (theme) => theme.palette.primary.main 
          }} />
        </Box>
        {isReadOnly ? (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
          </Typography>
        ) : (
          <Link 
            underline="none" 
            component={RouterLink} 
            to={`/team/${id}`}
            sx={{ 
              fontWeight: 500,
              color: (theme) => theme.palette.primary.main,
              '&:hover': {
                color: (theme) => theme.palette.primary.dark,
                textDecoration: 'underline'
              }
            }}
          >
            {value}
          </Link>
        )}
      </Box>
    ),
  },
  {
    field: "active",
    headerName: "Status",
    flex: 0.8,
    minWidth: 100,
    renderCell: ({ value }) => {
      const isActive = value?.toLowerCase() === "y";
      return (
        <Chip
          icon={<Icon icon={isActive ? "eva:checkmark-circle-outline" : "eva:close-circle-outline"} width={16} height={16} />}
          label={isActive ? "Active" : "Inactive"}
          size="small"
          color={isActive ? "success" : "error"}
          variant="filled"
          sx={{ fontWeight: 500, fontSize: '0.75rem' }}
        />
      );
    },
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1.5,
    minWidth: 200,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <Typography variant="body2" sx={{ 
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {value}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "creationTime",
    headerName: "Created On",
    flex: 1,
    minWidth: 160,
    renderCell: ({ value }) => {
      const isRecent = new Date(value) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Within 7 days
      const isOld = new Date(value) < new Date(Date.now() - 365 * 24 * 60 * 60 * 1000); // Older than 1 year
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
            p: 0.5,
            borderRadius: 1,
            background: isRecent 
              ? (theme) => theme.palette.success.lighter
              : isOld 
                ? (theme) => theme.palette.warning.lighter
                : (theme) => theme.palette.info.lighter,
            mr: 1,
            display: 'flex',
            alignItems: 'center'
          }}>
            <Icon 
              icon={isRecent ? "eva:clock-outline" : isOld ? "eva:calendar-outline" : "eva:time-outline"} 
              width={14} 
              height={14} 
              style={{ 
                color: isRecent 
                  ? (theme) => theme.palette.success.main
                  : isOld 
                    ? (theme) => theme.palette.warning.main
                    : (theme) => theme.palette.info.main
              }} 
            />
          </Box>
          <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                color: isRecent 
                  ? (theme) => theme.palette.success.main
                  : isOld 
                    ? (theme) => theme.palette.warning.main
                    : (theme) => theme.palette.text.primary
              }}
            >
              {yearFirstDateTimeFormat(value)}
            </Typography>
          </Tooltip>
        </Box>
      );
    },
  },
];

export const teamMembersColumns = (actionRequired, enableRedirection) => [
  {
    field: "username",
    headerName: "User Name",
    width: 150,
    renderCell: ({ value, id }) => (
      <>
        {enableRedirection ? (
          <Link underline="none" component={RouterLink} to={`/team/${id}`}>
            {value}
          </Link>
        ) : (
          value
        )}
      </>
    ),
  },
  {
    field: "firstname",
    headerName: "Name",
    width: 120,
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
    minWidth: 200,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: "accesstype",
    headerName: "Access Type",
    width: 120,
  },
  {
    field: "Actions",
    width: 50,
    sortable: false,
    headerName: "",
    renderCell: ({ id, row }) => {
      return <TeamMembersActionMenu id={id} row={row} />;
    },
    hide: !actionRequired,
  },
];

export const teamPayoutsColumns = (actionRequired) => [
  {
    field: "payoutId",
    headerName: "ID",
    flex: 0.5,
  },
  {
    field: "periodStartDate",
    headerName: "From",
    flex: 0.7,
  },
  {
    field: "displayEndDate",
    headerName: "To",
    flex: 0.7,
  },
  {
    field: "totalAmountCharged",
    headerName: "Total Collection",
    flex: 0.5,
  },
  {
    field: "CPO_GST",
    headerName: "CPO GST",
    flex: 0.5,
  },
  {
    field: "totalGSTOnAmount",
    headerName: "Total GST ",
    flex: 0.5,
  },
  {
    field: "amikComission",
    headerName: "AMIK Comission",
    flex: 0.5,
  },
  {
    field: "GSTOnAmikComission",
    headerName: "AMIK GST",
    flex: 0.5,
  },
  {
    field: "CPOPayout",
    headerName: "Payout",
    flex: 0.5,
  },
  // {
  //   field: "totalAmountCharged",
  //   headerName: "totalAmountCharged",
  //    flex: 0.5,
  // },

  // {
  //   field: "unitsConsumed",
  //   headerName: "unitsConsumed",
  //    flex: 0.5,
  // },

  {
    field: "Actions",
    width: 50,
    sortable: false,
    headerName: "",
    renderCell: ({ id, row }) => {
      return <TeamPayoutActionMenu id={id} row={row} />;
    },
  },
];

export const teamsFormTabs = [
  { value: "general", label: "GENERAL" },
  { value: "payout accounts", label: "PAYOUT ACCOUNTS" },
  { value: "address", label: "ADDRESS" },
  { value: "members", label: "TEAM MEMBERS" },
  { value: "finance", label: "FINANCE CONFIGURATION" },
  { value: "financeHistory", label: "FINANCE HISTORY" },
  { value: "carbon", label: "CARBON FOOTPRINT" },
  // { value: "payout", label: "PAYOUTS" },
];

export const initialPayoutAccountObj = {
  business_name: "",
  business_type: "",
  beneficiary_name: "",
  email: "",
  phone: "",
  account_no: "",
  account_type: "",
  ifsc_code: "",
  gst: "",
  payout_percentage: 0,
  tax_percentage: 0,
  creation: true,
  deleteBtn: true,
};

export const initialFinanceConfigObj = {
  charger_payment_details: {
    monthly_payment: true,
    dc_charges_monthly: 349,
    ac_charges_monthly: 249,
    dc_charges_annual: "",
    ac_charges_annual: "",
    tax: 18,
    other_fixed_cost: "",
  },
  transaction_payment_details: {
    pg_charges_bearer: "EV",
    payment_gateway_percent: 2.36,
    tax: 18,
  },
  payout_details: {
    payout_rule: 3,
    tax_bearer: "CM",
    tax: 18,
    chargemiles_commission_in_percent: true,
    chargemiles_commission_percent: 5,
    chargemiles_commission_cost: "",
  },
  payment_gateway_details: {
    payment_gateway_details_id: null,
  },
};

export const possibleBusinessTypes = [
  { label: "Individual", value: "individual" },
  { label: "Proprietorship", value: "proprietorship" },
  { label: "Partnership", value: "partnership" },
  { label: "Private Limited", value: "private_limited" },
  { label: "Public Limited", value: "public_limited" },
  { label: "Limited Liability Partnership", value: "llp" },
  { label: "NonGovernmental Organization", value: "ngo" },
  { label: "Trust", value: "trust" },
  { label: "Society", value: "society" },
  { label: "Not Yet Registered", value: "not_yet_registered" },
  { label: "Hindu Undivided Family", value: "huf" },
];

export const possiblePayoutRules = [
  {
    label: "Fixed Amount per month or % per Unit (whichever is higher)",
    value: 1,
  },
  {
    label: "Fixed Amount per month or fixed per Unit (whichever is higher)",
    value: 2,
  },
  { label: "Fixed Amount per month and % per Unit", value: 3 },
  { label: "Fixed Amount per month and fixed per Unit", value: 4 },
  { label: "% per Unit", value: 5 },
  { label: "fixed per Unit", value: 6 },
  { label: "Fixed Amount per month", value: 7 },
];

export const getTeamDefaultValues = () => ({
  name: "",
  active: "Y",
  description: "",
  payoutAccounts: [initialPayoutAccountObj],
  financeConfig: initialFinanceConfigObj,
  locationLatitude: "",
  locationLongitude: "",
  street: "",
  address: {
    addressId: "",
    street: "",
    houseNumber: "",
    zipCode: "",
    city: "",
    country: "",
    lat: "",
    lng: "",
  },
});
